import { Loader2, RefreshCcw, Ruler } from "lucide-react";
import { useExtractMeasurements, useMeasurements } from "@/hooks/use-measurements";
import { Button } from "@/components/ui/button";

function eur(n: number | null): string {
  if (n == null) return "—";
  return n.toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function MeasurementsSection({ projectId }: { projectId: string }) {
  const query = useMeasurements(projectId);
  const extract = useExtractMeasurements(projectId);

  const data = query.data;
  const items = data?.items ?? [];

  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="max-w-2xl">
          <h2 className="text-sm font-heading font-semibold text-ink flex items-center gap-2">
            <Ruler className="w-4 h-4 text-content-muted" />
            Mediciones del proyecto
          </h2>
          <p className="text-xs text-content-muted mt-1">
            Las mediciones del presupuesto se extraen <strong>automáticamente</strong>{" "}
            al procesar los documentos. Aquí puedes verlas y verificarlas (partidas,
            cantidades e importes). Es la base para comparar lo previsto con lo facturado.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => extract.mutate()}
          disabled={extract.isPending}
          title="Reprocesa los presupuestos (solo si hace falta tras una mejora)"
          className="text-content-muted"
        >
          {extract.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCcw className="w-4 h-4" />
          )}
          Re-extraer
        </Button>
      </div>

      {extract.isPending && (
        <p className="text-xs text-content-muted">
          Clasificando documentos y extrayendo partidas… puede tardar. No cierres
          la página.
        </p>
      )}

      {extract.data && !extract.isPending && (
        <div className="bg-energia-pale border border-line rounded-md px-3 py-2 text-xs text-ink">
          Última extracción: <strong>{extract.data.partidas}</strong> partidas ·{" "}
          {extract.data.presupuestos} presupuesto(s) · {extract.data.procesados}{" "}
          documentos analizados · {extract.data.omitidos_sin_texto} sin texto.
        </div>
      )}

      {data && data.documentos.length > 0 && (
        <div className="bg-surface rounded-md border border-line p-3 space-y-1.5">
          <p className="text-[11px] font-medium text-content-muted uppercase">
            Documentos detectados como presupuesto/mediciones
          </p>
          {data.documentos.map((d) => (
            <div key={d.document_id} className="flex items-center justify-between gap-3 text-xs">
              <span className="text-ink truncate" title={d.filename ?? undefined}>
                {d.filename ?? d.document_id}
              </span>
              <span
                className={
                  d.con_cantidad > 0
                    ? "text-energia whitespace-nowrap font-medium"
                    : "text-content-muted whitespace-nowrap"
                }
              >
                {d.partidas} partidas · {d.con_cantidad} con cantidad
              </span>
            </div>
          ))}
          <p className="text-[11px] text-content-muted pt-1">
            Para la comparativa sirven las partidas <strong>con cantidad</strong>{" "}
            (las mediciones). Las que solo tienen precio son un cuadro de precios.
          </p>
        </div>
      )}

      {query.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 text-content-muted animate-spin" />
        </div>
      ) : query.isError ? (
        <div className="bg-danger/10 border border-danger/30 rounded-md p-3 text-center text-sm text-danger">
          {query.error?.message ?? "Error cargando las mediciones."}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 border-2 border-dashed border-line-strong rounded-md">
          <Ruler className="w-8 h-8 text-content-muted/50 mx-auto mb-2" />
          <p className="text-sm text-content font-medium">Sin mediciones todavía</p>
          <p className="text-xs text-content-muted mt-0.5">
            Aparecerán aquí automáticamente al procesar un documento de
            presupuesto/mediciones del proyecto.
          </p>
        </div>
      ) : (
        <div className="bg-surface rounded-lg border border-line overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-line">
                  <th className="text-left px-4 py-2 text-[11px] font-medium text-content-muted uppercase hidden md:table-cell">Código</th>
                  <th className="text-left px-4 py-2 text-[11px] font-medium text-content-muted uppercase">Descripción</th>
                  <th className="text-right px-4 py-2 text-[11px] font-medium text-content-muted uppercase">Cantidad</th>
                  <th className="text-right px-4 py-2 text-[11px] font-medium text-content-muted uppercase hidden lg:table-cell">P. unitario</th>
                  <th className="text-right px-4 py-2 text-[11px] font-medium text-content-muted uppercase">Importe</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line/60">
                {items.map((p) => (
                  <tr key={p.id} className="hover:bg-surface-100">
                    <td className="px-4 py-2 text-xs text-content-muted hidden md:table-cell whitespace-nowrap">{p.codigo ?? "—"}</td>
                    <td className="px-4 py-2 text-sm text-ink max-w-[48ch]" title={p.descripcion ?? undefined}>{p.descripcion ?? "—"}</td>
                    <td className="px-4 py-2 text-sm text-content text-right tabular-nums whitespace-nowrap">
                      {p.cantidad == null ? "—" : `${p.cantidad} ${p.unidad ?? ""}`.trim()}
                    </td>
                    <td className="px-4 py-2 text-sm text-content text-right tabular-nums hidden lg:table-cell">{eur(p.precio_unit)}</td>
                    <td className="px-4 py-2 text-sm text-content text-right tabular-nums">{eur(p.importe)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-surface-100 border-t-2 border-line font-semibold">
                  <td className="px-4 py-2.5 text-sm text-ink" colSpan={4}>
                    Total ({data?.count ?? items.length} partidas · {data?.documentos_presupuesto ?? 0} presupuesto/s)
                  </td>
                  <td className="px-4 py-2.5 text-sm text-ink text-right tabular-nums">
                    {eur(data?.total_importe ?? 0)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}
